import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import EventTimeline from "@/components/feature/EventTimeline";
import TopicCard from "@/components/feature/TopicCard";
import useSeo from "@/hooks/useSeo";
import { getCategoryById, getTopicById, hotRanking, topics } from "@/mocks/topics";

const sections = [
  { id: "gist", label: "事件梗概" },
  { id: "timeline", label: "完整时间线" },
  { id: "voices", label: "各方视角" },
  { id: "truth", label: "真相与深层原因" },
];

export default function TopicDetail() {
  const { id = "" } = useParams();
  const topic = getTopicById(id);
  const category = topic ? getCategoryById(topic.categoryId) : undefined;

  useSeo({
    title: topic
      ? `${topic.title} | 黑料热点事件时间线复盘`
      : "事件未找到 | 黑料热点",
    description: topic
      ? `${topic.summary} 本篇梳理了事件完整时间线、各方回应视角与背后深层原因。`
      : "抱歉，你访问的事件复盘不存在或已调整，请返回列表查看其他热点事件的完整时间线。",
    keywords: topic ? topic.keywords : "黑料热点,事件复盘",
    canonicalPath: `/topic/${id}`,
    ogType: "article",
    jsonLd: topic
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: topic.title,
            description: topic.summary,
            image: topic.cover,
            datePublished: topic.publishedAt,
            dateModified: topic.publishedAt,
            articleSection: category?.name,
            keywords: topic.tags.join(","),
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: "黑料热点" },
            publisher: { "@type": "Organization", name: "黑料热点" },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "/" },
              { "@type": "ListItem", position: 2, name: "热点复盘", item: "/topics" },
              { "@type": "ListItem", position: 3, name: topic.title, item: `/topic/${topic.id}` },
            ],
          },
        ]
      : undefined,
  });

  if (!topic) {
    return (
      <div className="min-h-screen w-full bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-unknow-line text-4xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            没有找到这篇复盘
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            该事件可能已调整标题或合并到其他复盘，你可以回到列表查看全部已完成时间线梳理的事件。
          </p>
          <Link
            to="/topics"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回复盘列表
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = topics
    .filter((item) => item.id !== topic.id)
    .sort((a, b) => (a.categoryId === topic.categoryId ? -1 : 1) - (b.categoryId === topic.categoryId ? -1 : 1))
    .slice(0, 3);

  return (
    <div className="min-h-screen w-full bg-background-50">
      <SiteHeader />
      <main>
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "热点复盘", to: "/topics" },
                { label: category?.name ?? "事件复盘" },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {category && (
                <span className="inline-flex items-center gap-1 rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                  <i className={category.icon}></i>
                  {category.name}
                </span>
              )}
              <span className="text-xs text-foreground-500">更新于 {topic.publishedAt}</span>
              <span className="flex items-center gap-1 text-xs text-primary-600">
                <i className="ri-fire-line"></i>
                热度 {topic.heat}
              </span>
            </div>
            <h1 className="mt-3 max-w-4xl font-heading text-2xl font-black leading-tight tracking-tight text-foreground-950 md:text-4xl">
              {topic.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-voice-line"></i>
                黑料热点编辑部
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {topic.readMinutes} 分钟读完
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-node-tree"></i>
                {topic.timeline.length} 个时间线节点
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-links-line"></i>
                引用 {topic.sources} 个信源
              </span>
            </div>
          </div>
        </section>

        <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-10 px-4 py-10 md:px-8 md:py-14 lg:grid-cols-12">
          <article className="lg:col-span-8">
            <div className="h-56 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-80">
              <img
                src={topic.cover}
                alt={`${topic.title} 事件复盘封面`}
                title={`${topic.title} - 热点事件时间线复盘`}
                className="h-full w-full object-top object-cover"
              />
            </div>

            <section id="gist" className="scroll-mt-24 pt-8">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                事件梗概
              </h2>
              <p className="mt-3 text-base leading-relaxed text-foreground-700">{topic.summary}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                下面的内容按时间顺序整理了从事件起因到最新回应的关键节点，并并列呈现各方立场。
                需要提醒的是，部分说法仍在核实中，阅读时建议结合页面标注的信息来源一起判断。
              </p>
            </section>

            <section id="timeline" className="scroll-mt-24 pt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                完整时间线
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                按发生顺序排列的关键节点，帮助你理解事件是如何一步步发酵的。
              </p>
              <EventTimeline nodes={topic.timeline} />
            </section>

            <section id="voices" className="scroll-mt-24 pt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                各方视角
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                同一件事，不同立场的人看到的东西并不相同。以下为各方公开表达的核心观点。
              </p>
              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
                {topic.perspectives.map((p) => (
                  <div
                    key={p.name}
                    className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-5"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                      <i className="ri-user-3-line text-xl"></i>
                    </span>
                    <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{p.name}</h3>
                    <span className="mt-0.5 text-xs text-foreground-500">{p.role}</span>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-700">{p.stance}</p>
                  </div>
                ))}
              </div>
            </section>

            <section id="truth" className="scroll-mt-24 pt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                真相与深层原因
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                把事实、说法与推测分开之后，我们能看到的往往不是「谁对谁错」，而是问题出在哪个环节。
              </p>
              <div className="mt-5 space-y-4">
                {topic.analysis.map((block, index) => (
                  <div
                    key={block.title}
                    className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 text-sm font-bold text-accent-800">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-950">
                        {block.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{block.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="text-sm text-foreground-500">相关标签：</span>
              {topic.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-md bg-background-100 px-3 py-1 text-xs text-foreground-700"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 text-xs leading-relaxed text-foreground-500">
              内容说明：本文为事件脉络整理与公开观点引用，不代表对任何一方的定论。
              如发现信息有误，欢迎通过「关于我们」页面的邮箱反馈，我们核实后会更新并保留修改说明。
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-6">
              <nav aria-label="本页目录" className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">本页目录</h2>
                <ul className="mt-3 space-y-2 text-sm">
                  {sections.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className="flex items-center gap-2 text-foreground-600 transition-colors hover:text-primary-600"
                      >
                        <i className="ri-subtract-line text-foreground-300"></i>
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">热度榜</h2>
                <ul className="mt-3 space-y-3">
                  {hotRanking
                    .filter((item) => item.id !== topic.id)
                    .slice(0, 5)
                    .map((item, index) => (
                      <li key={item.id} className="flex items-center gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-100 text-xs font-bold text-foreground-700">
                          {index + 1}
                        </span>
                        <Link
                          to={`/topic/${item.id}`}
                          className="min-w-0 flex-1 truncate text-sm text-foreground-700 transition-colors hover:text-primary-600"
                          title={item.title}
                        >
                          {item.title}
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-accent-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">继续追踪</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  事件若有新回应，我们会更新这条时间线。你可以订阅更新，及时收到复盘修订提醒。
                </p>
                <Link
                  to="/about"
                  className="mt-4 inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                >
                  订阅与反馈
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <section className="w-full border-t border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-14">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              相关复盘推荐
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <TopicCard key={item.id} topic={item} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}